// Firestore data hooks used by the dashboard. Wrap react-firebase-hooks
// (useCollection / useDocument) with our path conventions and shape mapping.
//
// All hooks return a stable triple { data, loading, error } so consumers don't
// have to unpack the underlying snapshot type.
//
// Path conventions (mirror firestore.rules):
//   teams/{teamId}
//   teams/{teamId}/members/{uid}      doc body: { uid, role, joinedAt, email, displayName }
//   teams/{teamId}/invites/{inviteId} doc body: { invitedEmail, role, invitedBy, createdAt }
//   teams/{teamId}/projects/{projectId}
//   teams/{teamId}/projects/{projectId}/flows/{flowId}  doc body: { name, yaml, tags, updatedAt }
//   teams/{teamId}/projects/{projectId}/runs/{runId}
//   teams/{teamId}/projects/{projectId}/scans/{scanId}/drifts/{driftId}

import {
  collection,
  collectionGroup,
  doc,
  documentId,
  orderBy,
  query,
  where,
} from 'firebase/firestore'
import { useCollection, useDocument } from 'react-firebase-hooks/firestore'
import { db } from './firebase.js'

/**
 * Firestore path segment that may be absent before the caller has resolved it
 * (e.g. a teamId from a route param that hasn't loaded yet).
 * @typedef {string | null | undefined} Id
 */

/**
 * A Firestore doc flattened to a plain object. The field set is whatever the
 * document holds (no compile-time schema at this layer), hence the open
 * `[key: string]: any` index signature alongside the synthetic `id`/`_path`.
 * @typedef {{ id: string, _path: string, [key: string]: any }} DocRecord
 */

/**
 * @param {import('firebase/firestore').QuerySnapshot | undefined} snapshot
 * @returns {DocRecord[]}
 */
function unwrapCollection(snapshot) {
  if (!snapshot) return []
  return snapshot.docs.map((d) => ({ id: d.id, _path: d.ref.path, ...d.data() }))
}

/**
 * @param {import('firebase/firestore').DocumentSnapshot | undefined} snapshot
 * @returns {DocRecord | null}
 */
function unwrapDoc(snapshot) {
  if (!snapshot?.exists()) return null
  return { id: snapshot.id, _path: snapshot.ref.path, ...snapshot.data() }
}

/**
 * Teams the current user is a member of. Collection-group query over the
 * `members` subcollection filtered by uid. Pair with the index in
 * firestore.indexes.json (members.uid collection group).
 * @param {Id} uid
 */
export function useMyTeams(uid) {
  const q = uid
    ? query(collectionGroup(db, 'members'), where('uid', '==', uid), orderBy('joinedAt', 'desc'))
    : null
  const [snap, loading, error] = useCollection(q)
  const memberships = unwrapCollection(snap)
  // teamId is the parent of the parent of the member doc:
  // teams/{teamId}/members/{uid} → parent.parent.id
  const teams = memberships
    .map((m) => {
      const teamId = m._path.split('/')[1]
      return { teamId, role: m.role, joinedAt: m.joinedAt }
    })
    .filter((t) => t.teamId)
  return { data: teams, loading, error }
}

/** @param {Id} teamId */
export function useTeam(teamId) {
  const ref = teamId ? doc(db, 'teams', teamId) : null
  const [snap, loading, error] = useDocument(ref)
  return { data: unwrapDoc(snap), loading, error }
}

/** @param {Id} teamId */
export function useTeamMembers(teamId) {
  const q = teamId ? collection(db, 'teams', teamId, 'members') : null
  const [snap, loading, error] = useCollection(q)
  return { data: unwrapCollection(snap), loading, error }
}

/** @param {Id} teamId */
export function useTeamInvites(teamId) {
  const q = teamId ? collection(db, 'teams', teamId, 'invites') : null
  const [snap, loading, error] = useCollection(q)
  return { data: unwrapCollection(snap), loading, error }
}

/** @param {Id} teamId */
export function useProjects(teamId) {
  const q = teamId ? collection(db, 'teams', teamId, 'projects') : null
  const [snap, loading, error] = useCollection(q)
  return { data: unwrapCollection(snap), loading, error }
}

/**
 * @param {Id} teamId
 * @param {Id} projectId
 */
export function useProject(teamId, projectId) {
  const ref = teamId && projectId ? doc(db, 'teams', teamId, 'projects', projectId) : null
  const [snap, loading, error] = useDocument(ref)
  return { data: unwrapDoc(snap), loading, error }
}

/**
 * @param {Id} teamId
 * @param {Id} projectId
 */
export function useFlows(teamId, projectId) {
  const q =
    teamId && projectId ? collection(db, 'teams', teamId, 'projects', projectId, 'flows') : null
  const [snap, loading, error] = useCollection(q)
  return { data: unwrapCollection(snap), loading, error }
}

/**
 * @param {Id} teamId
 * @param {Id} projectId
 * @param {Id} flowId
 */
export function useFlow(teamId, projectId, flowId) {
  const ref =
    teamId && projectId && flowId
      ? doc(db, 'teams', teamId, 'projects', projectId, 'flows', flowId)
      : null
  const [snap, loading, error] = useDocument(ref)
  return { data: unwrapDoc(snap), loading, error }
}

/**
 * All runs for a project, ordered by startedAt desc. Used by RunsList.
 * @param {Id} teamId
 * @param {Id} projectId
 */
export function useRuns(teamId, projectId) {
  const q =
    teamId && projectId
      ? query(
          collection(db, 'teams', teamId, 'projects', projectId, 'runs'),
          orderBy('startedAt', 'desc'),
        )
      : null
  const [snap, loading, error] = useCollection(q)
  return { data: unwrapCollection(snap), loading, error }
}

/**
 * A single run doc for the run detail page.
 * @param {Id} teamId
 * @param {Id} projectId
 * @param {Id} runId
 */
export function useRun(teamId, projectId, runId) {
  const ref =
    teamId && projectId && runId
      ? doc(db, 'teams', teamId, 'projects', projectId, 'runs', runId)
      : null
  const [snap, loading, error] = useDocument(ref)
  return { data: unwrapDoc(snap), loading, error }
}

/**
 * All scans for a project, ordered by startedAt desc. Used by DriftReportView.
 * @param {Id} teamId
 * @param {Id} projectId
 */
export function useScans(teamId, projectId) {
  const q =
    teamId && projectId
      ? query(
          collection(db, 'teams', teamId, 'projects', projectId, 'scans'),
          orderBy('startedAt', 'desc'),
        )
      : null
  const [snap, loading, error] = useCollection(q)
  return { data: unwrapCollection(snap), loading, error }
}

/**
 * Drifts under a single scan. Used by DriftReportView's expanded view.
 * @param {Id} teamId
 * @param {Id} projectId
 * @param {Id} scanId
 */
export function useScanDrifts(teamId, projectId, scanId) {
  const q =
    teamId && projectId && scanId
      ? query(
          collection(db, 'teams', teamId, 'projects', projectId, 'scans', scanId, 'drifts'),
          orderBy('confidence', 'desc'),
        )
      : null
  const [snap, loading, error] = useCollection(q)
  return { data: unwrapCollection(snap), loading, error }
}

/**
 * Live step events for a run, ordered by seq. The FirestoreReporter writes
 * each ReporterEvent as a doc here; react-firebase-hooks gives us a
 * snapshot listener so the UI updates in sub-1s.
 * @param {Id} teamId
 * @param {Id} projectId
 * @param {Id} runId
 */
export function useRunSteps(teamId, projectId, runId) {
  const q =
    teamId && projectId && runId
      ? query(
          collection(db, 'teams', teamId, 'projects', projectId, 'runs', runId, 'steps'),
          orderBy('seq', 'asc'),
        )
      : null
  const [snap, loading, error] = useCollection(q)
  return { data: unwrapCollection(snap), loading, error }
}

/**
 * Latest run per flow, used by the FlowsList page to render status badges.
 * Queries the runs subcollection; we filter client-side to "latest per flow"
 * since the data volume is small in V1.
 * @param {Id} teamId
 * @param {Id} projectId
 */
export function useLatestRunsByFlow(teamId, projectId) {
  const q =
    teamId && projectId
      ? query(
          collection(db, 'teams', teamId, 'projects', projectId, 'runs'),
          orderBy('startedAt', 'desc'),
        )
      : null
  const [snap, loading, error] = useCollection(q)
  const runs = unwrapCollection(snap)
  const latestByFlow = new Map()
  for (const run of runs) {
    if (!latestByFlow.has(run.flowId)) latestByFlow.set(run.flowId, run)
  }
  return { data: latestByFlow, loading, error }
}

/**
 * Outstanding drift counts per flow, used for FlowsList status badges.
 * @param {Id} teamId
 * @param {Id} projectId
 */
export function useDriftCountsByFlow(teamId, projectId) {
  const q =
    teamId && projectId ? query(collectionGroup(db, 'drifts'), where('teamId', '==', teamId)) : null
  const [snap, loading, error] = useCollection(q)
  const drifts = unwrapCollection(snap).filter((d) => d.projectId === projectId)
  const counts = new Map()
  for (const drift of drifts) {
    counts.set(drift.flowId, (counts.get(drift.flowId) ?? 0) + 1)
  }
  return { data: counts, loading, error }
}

/**
 * Drift counts at the project level (for switcher attention pips).
 * @param {Id} teamId
 * @param {Id} projectId
 */
export function useProjectAttentionCount(teamId, projectId) {
  const { data: drifts } = useDriftCountsByFlow(teamId, projectId)
  let total = 0
  for (const count of drifts.values()) total += count
  return total
}

/**
 * Used for the team switcher attention pip — sum of drift counts across all
 * projects in the team.
 * @param {Id} teamId
 */
export function useTeamAttentionCount(teamId) {
  const q = teamId ? query(collectionGroup(db, 'drifts'), where('teamId', '==', teamId)) : null
  const [snap] = useCollection(q)
  const drifts = unwrapCollection(snap)
  return drifts.length
}

/**
 * Current-month usage rollup for a team. Pair with usagePeriodId() to
 * compute the YYYYMM document id.
 * @param {Id} teamId
 * @param {Id} periodId
 */
export function useUsageMonth(teamId, periodId) {
  const ref = teamId && periodId ? doc(db, 'teams', teamId, 'usage', periodId) : null
  const [snap, loading, error] = useDocument(ref)
  return { data: unwrapDoc(snap), loading, error }
}

/**
 * Per-day usage subcollection for a given period (last-30-days breakdown).
 * @param {Id} teamId
 * @param {Id} periodId
 */
export function useUsageDays(teamId, periodId) {
  const q =
    teamId && periodId
      ? query(
          collection(db, 'teams', teamId, 'usage', periodId, 'daily'),
          orderBy('__name__', 'desc'),
        )
      : null
  const [snap, loading, error] = useCollection(q)
  return { data: unwrapCollection(snap), loading, error }
}

export function usagePeriodId(now = new Date()) {
  return `${now.getUTCFullYear()}${String(now.getUTCMonth() + 1).padStart(2, '0')}`
}

export function usageDayId(now = new Date()) {
  return `${usagePeriodId(now)}${String(now.getUTCDate()).padStart(2, '0')}`
}

export { documentId }
